<script lang="ts">
  import { router } from '@inertiajs/svelte';
  import { favoriteViews } from '../lib/stores/favorites';
  import { getTimelinePath } from '../lib/timelinePath';
  import { openGlobalSearch } from '../lib/stores/globalSearch';

  interface Props {
    activePage: 'timeline' | 'calendar' | 'projects' | 'boards' | 'cycles' | 'habits';
  }

  let { activePage }: Props = $props();

  const pages = {
    timeline: {
      icon: '📊',
      label: 'Timeline',
      description: 'Plan and track projects across weeks, months, and years.',
      shortcut: 'E',
      path: '/dashboard', // overridden in navigate() below
    },
    calendar: {
      icon: '📅',
      label: 'Calendar',
      description: 'Year-at-a-glance event calendar with multiple layouts.',
      shortcut: 'C',
      path: '/events_calendar',
    },
    projects: {
      icon: '📁',
      label: 'Projects',
      description: 'Browse, filter, and manage all your projects.',
      shortcut: 'P',
      path: '/projects',
    },
    boards: {
      icon: '🗺️',
      label: 'Boards',
      description: 'Arrange projects freely on an interactive canvas.',
      shortcut: 'B',
      path: '/boards',
    },
    cycles: {
      icon: '🔁',
      label: 'Cycles',
      description: 'Recurring checklists for your week, month, quarter, and year.',
      shortcut: 'Y',
      path: '/cycles',
    },
    habits: {
      icon: '🌱',
      label: 'Habits',
      description: 'Track daily habits and progress over time.',
      shortcut: 'H',
      path: '/habits',
    },
  } as const;

  type PageKey = keyof typeof pages;

  const pageKeys: PageKey[] = ['timeline', 'calendar', 'projects', 'boards', 'cycles', 'habits'];

  let active = $derived(pages[activePage]);

  // Keyboard + click accessible open/close state (hover still works via CSS)
  let isMenuOpen = $state(false);

  function toggleMenu() {
    isMenuOpen = !isMenuOpen;
  }

  function closeMenu() {
    isMenuOpen = false;
  }

  function navigate(key: PageKey) {
    if (key !== activePage) {
      closeMenu();
      // For timeline, go directly to a timeline URL to avoid /dashboard redirect
      // which could land on a calendar page if #1 favorite is a calendar view.
      const path = key === 'timeline' ? getTimelinePath($favoriteViews) : pages[key].path;
      router.get(path);
    }
  }

  function handleSearch() {
    closeMenu();
    openGlobalSearch();
  }

  function handleKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      closeMenu();
      // Re-focus the trigger after closing
      (e.currentTarget as HTMLElement)
        ?.closest('.nav-dropdown-wrapper')
        ?.querySelector<HTMLElement>('.nav-dropdown-trigger')
        ?.focus();
    }
  }

  function handleBlur(e: FocusEvent) {
    // Close when focus leaves the entire dropdown wrapper
    const wrapper = e.currentTarget as HTMLElement;
    // relatedTarget is the element receiving focus — if it's outside the wrapper, close
    if (!wrapper.contains(e.relatedTarget as Node)) {
      closeMenu();
    }
  }
</script>

<!-- svelte-ignore a11y_no_static_element_interactions -->
<div class="nav-dropdown-wrapper" onkeydown={handleKeydown} onfocusout={handleBlur}>
  <button
    class="nav-dropdown-trigger"
    type="button"
    onclick={toggleMenu}
    aria-expanded={isMenuOpen}
    aria-haspopup="true"
  >
    <span class="trigger-icon">{active.icon}</span>
    <span class="trigger-label">{active.label}</span>
    <svg class="nav-chevron" viewBox="0 0 10 6" fill="currentColor">
      <path d="M1 1l4 4 4-4" stroke="currentColor" stroke-width="1.2" fill="none" />
    </svg>
  </button>

  <div class="nav-dropdown-menu" class:open={isMenuOpen} role="menu">
    {#each pageKeys as key}
      {@const page = pages[key]}
      {@const isActive = key === activePage}
      {#if isActive}
        <div class="nav-dropdown-item active" role="menuitem" aria-current="page">
          <span class="item-icon">{page.icon}</span>
          <div class="item-text">
            <span class="item-label">{page.label}</span>
            <span class="item-description">
              {page.description} <kbd class="kbd-hint">{page.shortcut}</kbd>
            </span>
          </div>
        </div>
      {:else}
        <button
          class="nav-dropdown-item"
          type="button"
          role="menuitem"
          onclick={() => navigate(key)}
        >
          <span class="item-icon">{page.icon}</span>
          <div class="item-text">
            <span class="item-label">{page.label}</span>
            <span class="item-description">
              {page.description} <kbd class="kbd-hint">{page.shortcut}</kbd>
            </span>
          </div>
        </button>
      {/if}
    {/each}

    <div class="nav-dropdown-divider"></div>
    <button class="nav-dropdown-item" type="button" role="menuitem" onclick={handleSearch}>
      <span class="item-icon">⌕</span>
      <div class="item-text">
        <span class="item-label">Search</span>
        <span class="item-description"
          >Find projects and tasks <span class="kbd-hint"><kbd>K</kbd> or <kbd>/</kbd></span></span
        >
      </div>
    </button>
  </div>
</div>

<style>
  .nav-dropdown-wrapper {
    position: relative;
    display: inline-flex;
  }

  .nav-dropdown-trigger {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.1875rem 0.375rem;
    background: #f3f4f6;
    border: none;
    border-radius: 0.25rem;
    font-size: 0.6875rem;
    font-weight: 600;
    color: #1f2937;
    cursor: pointer;
    line-height: 1.4;
    font-family: inherit;
    height: 1.5rem;
  }

  .nav-dropdown-trigger:hover {
    background: #e5e7eb;
  }

  .trigger-icon {
    font-size: 0.75rem;
    line-height: 1;
  }

  .trigger-label {
    white-space: nowrap;
  }

  .nav-chevron {
    width: 0.5rem;
    height: 0.5rem;
    color: #6b7280;
    flex-shrink: 0;
  }

  .nav-dropdown-menu {
    position: absolute;
    top: 100%;
    left: 0;
    min-width: 14rem;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 0.375rem;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    z-index: 50;
    opacity: 0;
    visibility: hidden;
    transition:
      opacity 0.15s ease,
      visibility 0.15s ease;
    padding: 0.25rem 0;
  }

  /* Invisible bridge between trigger and menu to prevent hover gap */
  .nav-dropdown-menu::before {
    content: '';
    position: absolute;
    top: -0.5rem;
    left: 0;
    right: 0;
    height: 0.5rem;
  }

  .nav-dropdown-wrapper:hover .nav-dropdown-menu,
  .nav-dropdown-menu.open {
    opacity: 1;
    visibility: visible;
  }

  .nav-dropdown-item {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    width: 100%;
    padding: 0.5rem 0.75rem;
    border: none;
    background: none;
    cursor: pointer;
    text-align: left;
    font-family: inherit;
    transition: background 0.1s ease;
  }

  .nav-dropdown-item:hover:not(.active) {
    background: #f9fafb;
  }

  .nav-dropdown-item.active {
    background: #eff6ff;
    color: #2563eb;
    cursor: default;
  }

  .nav-dropdown-divider {
    height: 1px;
    margin: 0.25rem 0;
    background: #e5e7eb;
  }

  kbd {
    padding: 0.0625rem 0.25rem;
    font-size: 0.625rem;
    font-weight: 600;
    color: #6b7280;
    background: #f3f4f6;
    border: 1px solid #e5e7eb;
    border-radius: 0.25rem;
  }

  .item-icon {
    font-size: 0.875rem;
    line-height: 1.3;
    flex-shrink: 0;
  }

  .item-text {
    display: flex;
    flex-direction: column;
    gap: 0.0625rem;
    min-width: 0;
  }

  .item-label {
    font-size: 0.75rem;
    font-weight: 600;
    color: #1f2937;
    line-height: 1.4;
  }

  .nav-dropdown-item.active .item-label {
    color: #2563eb;
  }

  .item-description {
    font-size: 0.625rem;
    font-weight: 400;
    color: #6b7280;
    line-height: 1.4;
  }

  .nav-dropdown-item.active .item-description {
    color: #6b7280;
  }
</style>
